import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import { useOrders, formatMoney } from '@/services/orders';
import OrderModal from './components/order-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { ACCESSORY_ORDER_STATUSES, ORDER_SOURCE_LABELS } from '@/models/order';
import { formatDate } from '@/utils/helpers';
// One label map for the whole admin panel. There were three copies of it — this
// file, the order modal and utils/order-status — and the moment a status value
// is added, two of them go stale (CLAUDE.md pitfall #30).
import { ORDER_STATUS_LABELS, fulfilmentStatusLabel } from '@/utils/order-status';

const Orders = () => {
    const [page, setPage] = useState(1); // 1-indexed for the API
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    const { data: result, isLoading } = useOrders({ page, limit: rowsPerPage, status: status || undefined, search: search || undefined });
    const orders = result?.data?.orders ?? [];
    const total = result?.data?.total ?? 0;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>();

    const toggle = () => setIsOpen((prev) => !prev);
    const handleClick = (id: string) => {
        setSelectedOrderId(id);
        toggle();
    };

    const handleChangePage = (_: unknown, item: number) => setPage(item + 1); // TablePagination is 0-indexed
    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    const applySearch = () => {
        setSearch(searchInput.trim());
        setPage(1);
    };
    const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') applySearch();
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Orders</h3>
                    <h3 className="page-subhead subhead">Accessory orders — who bought what, and where it is in fulfillment.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {/* Search + status filter share one row on large screens.
                                Each MaterialTextField is width:100%, so a flex row would
                                wrap them onto separate lines — use the responsive grid
                                instead: side-by-side on lg+, stacked on small screens. */}
                            <Row className="align-items-center">
                                <Col xs={12} lg={7}>
                                    <MaterialTextField
                                        label="Search by order number or email (press Enter)"
                                        type="text"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyDown={onSearchKeyDown}
                                        onBlur={applySearch}
                                    />
                                </Col>
                                <Col xs={12} lg={5}>
                                    <MaterialTextField
                                        name="select"
                                        select
                                        label="Status"
                                        value={status}
                                        onChange={(e) => {
                                            setStatus(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <MaterialFormOption value="">All statuses</MaterialFormOption>
                                        {ACCESSORY_ORDER_STATUSES.map((s) => (
                                            <MaterialFormOption key={s} value={s}>
                                                {ORDER_STATUS_LABELS[s]}
                                            </MaterialFormOption>
                                        ))}
                                    </MaterialTextField>
                                </Col>
                            </Row>

                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead />
                                            <TableBody>
                                                {orders.map((d) => (
                                                    <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                        <TableCell component="th" scope="row" padding="none">
                                                            {d.orderNumber}
                                                        </TableCell>
                                                        <TableCell>{d.email}</TableCell>
                                                        <TableCell>{ORDER_SOURCE_LABELS[d.source ?? 'accessories'] ?? d.source}</TableCell>
                                                        <TableCell>{formatMoney(d.totalAmount, d.currency)}</TableCell>
                                                        <TableCell>{fulfilmentStatusLabel(d.status)}</TableCell>
                                                        <TableCell>{d.lineItems?.reduce((n, li) => n + li.quantity, 0) ?? 0}</TableCell>
                                                        <TableCell>{formatDate(d.created_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {orders.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7} className="text-center">
                                                            No orders found.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={total}
                                        rowsPerPage={rowsPerPage}
                                        page={page - 1}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[20, 50, 100]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <OrderModal isOpen={isOpen} toggle={toggle} selectedOrderId={selectedOrderId} />
        </Container>
    );
};

export default Orders;
